import Dexie from 'dexie';

/**
 * Database schema version history
 * Each version upgrade should be additive to maintain backwards compatibility
 */

export const CURRENT_VERSION = 2;

/**
 * Apply schema to a Dexie database instance
 * @param {Dexie} db - Dexie database instance
 */
export function applySchema(db) {
  // V1: initial schema
  db.version(1).stores({
    cases: '_id, creator, updatedAt, revision',
    jurors: '_id, caseId, seatNumber, alternateSeatNumber, revision, [caseId+seatNumber], [caseId+alternateSeatNumber]',
    pendingOperations: '++id, entityType, entityId, operationType, caseId, createdAt',
    syncMetadata: 'key'
  });

  // V2: add compound index [entityType+entityId] for efficient lookups
  db.version(2).stores({
    cases: '_id, creator, updatedAt, revision',
    jurors: '_id, caseId, seatNumber, alternateSeatNumber, revision, [caseId+seatNumber], [caseId+alternateSeatNumber]',
    pendingOperations: '++id, [entityType+entityId], entityType, entityId, operationType, caseId, createdAt',
    syncMetadata: 'key'
  });

  return db;
}

/**
 * Case record structure
 * @typedef {Object} CaseRecord
 * @property {string} _id - Server-assigned ID or local UUID
 * @property {string} name - Case name
 * @property {string} [number] - Case number
 * @property {string} [details] - Case details
 * @property {string} creator - Creator's email
 * @property {number} juryBoxSize - Number of seats in jury box
 * @property {number} jurorsPerRow - Jurors per row in display
 * @property {number} numberOfAlternates - Number of alternate jurors
 * @property {number[]} spacers - Spacer positions
 * @property {string[]} demographicCategories - Demographic categories
 * @property {number} prosecutionPeremptStrikes - Available prosecution strikes
 * @property {number} defendantPeremptStrikes - Available defense strikes
 * @property {number} revision - Server revision number
 * @property {string} createdAt - ISO timestamp
 * @property {string} updatedAt - ISO timestamp
 * @property {boolean} [_localOnly] - True if not yet synced to server
 * @property {string} [_localId] - Original local ID if created offline
 */

/**
 * Juror record structure
 * @typedef {Object} JurorRecord
 * @property {string} _id - Server-assigned ID or local UUID
 * @property {string} caseId - Parent case ID
 * @property {string} [nameAndId] - Name and ID combined
 * @property {number} [seatNumber] - Seat in jury box (null if in pool)
 * @property {number} [alternateSeatNumber] - Alternate seat number
 * @property {string} [comments] - Comments/notes
 * @property {Object} [commentsStyles] - Rich text styles for comments
 * @property {Array} demographics - Array of {name, value} demographic entries
 * @property {boolean} [discarded] - Whether juror is dismissed
 * @property {boolean} [alternateDiscarded] - Whether alternate is dismissed
 * @property {string} [discardedReason] - 'peremptory' | 'cause' | 'hardship'
 * @property {string} [discarder] - 'prosecutor' | 'defense' | 'both'
 * @property {string} [color] - Background color
 * @property {number} revision - Server revision number
 * @property {string} created - ISO timestamp
 * @property {string} updated - ISO timestamp
 * @property {boolean} [_localOnly] - True if not yet synced to server
 * @property {string} [_localId] - Original local ID if created offline
 */

/**
 * Pending operation structure
 * @typedef {Object} PendingOperation
 * @property {number} id - Auto-incremented ID
 * @property {string} entityType - 'case' | 'juror'
 * @property {string} entityId - ID of the entity
 * @property {string} [caseId] - Parent case ID (for jurors)
 * @property {string} operationType - 'create' | 'update' | 'delete'
 * @property {Object} [diff] - Changed fields (for updates)
 * @property {Object} [data] - Full data (for creates)
 * @property {number} baseRevision - Revision this change is based on
 * @property {string} createdAt - ISO timestamp
 */

/**
 * Sync metadata structure
 * @typedef {Object} SyncMetadata
 * @property {string} key - Metadata key
 * @property {*} value - Metadata value
 */

export default { applySchema, CURRENT_VERSION };
